import { Identify, identify } from '@amplitude/analytics-browser';
import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { getNetworkInfo, NetworkInfo } from '../utils/platform/getNetworkInfo';

// Known User Types
// OPERATOR_AUDIENCE,
// STAFF_AUDIENCE,
// TEAM_MBR_AUDIENCE,
// CONTRACTOR_AUDIENCE

export function formatAudience(aud: string) {
  return aud
    .replace(/_/g, ' ')
    .replaceAll(
      /\S*/g,
      (word: string) => `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`
    );
}

const CATEGORICAL_NETWORK_FIELDS = [
  'online',
  'type',
  'effectiveType',
  'saveData',
  'isSupported',
] as const;

export type CategoricalNetworkInfo = Pick<
  NetworkInfo,
  (typeof CATEGORICAL_NETWORK_FIELDS)[number]
>;

export function getCategoricalNetworkInfo(): CategoricalNetworkInfo {
  const info = getNetworkInfo();
  return {
    online: info.online,
    type: info.type,
    effectiveType: info.effectiveType,
    saveData: info.saveData,
    isSupported: info.isSupported,
  };
}

export function categoricalNetworkInfoEquals(
  a: CategoricalNetworkInfo,
  b: CategoricalNetworkInfo
): boolean {
  return CATEGORICAL_NETWORK_FIELDS.every(field => a[field] === b[field]);
}

function setNetworkProps(identifyUser: Identify, info: CategoricalNetworkInfo) {
  for (const [field, value] of Object.entries(info)) {
    // Unset (not skip) so a stale saveData from an earlier session can't survive into Safari.
    if (value === null) {
      identifyUser.unset(`network_${field}`);
      continue;
    }
    identifyUser.set(`network_${field}`, value);
  }
}

export function setAmplitudeUserProps(audience: string, flags: LDFlagSet) {
  if (!audience) return;
  const identifyUser = new Identify();
  identifyUser.set('audience', formatAudience(audience));
  for (const [key, value] of Object.entries(flags)) {
    identifyUser.set(`ff_${key}`, value);
  }
  identify(identifyUser);
}

// Sole writer of the network user properties, on sign-in and on every later change.
export function setAmplitudeNetworkProps() {
  const identifyUser = new Identify();
  setNetworkProps(identifyUser, getCategoricalNetworkInfo());
  identify(identifyUser);
}
